import type { AccountId } from "@redotech/effect-api-shared/ids/definitions";
import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { useQuery } from "@tanstack/react-query";
import { Effect } from "effect";

/**
 * Fetch integrations for the caller's home team (default) or for an explicit
 * connected-team `accountId` when viewing a ticket that lives on another
 * connected team. The composer and thread-detail surfaces pass the ticket's
 * accountId so the send-block rules and signature pickers see the right
 * team's email integrations.
 */
export function useIntegrations(options?: {
  includeDeleted?: boolean;
  accountId?: AccountId | null;
  enabled?: boolean;
  staleTime?: number;
}) {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const includeDeleted = options?.includeDeleted ?? false;
  const accountId = options?.accountId ?? null;

  return useQuery({
    enabled: options?.enabled,
    staleTime: options?.staleTime,
    queryKey: ["integrations", includeDeleted, accountId],
    queryFn: async () => {
      const client = effectApi.getClient();

      if (accountId) {
        const result = await effectApi.runPromise(
          client.integration
            .getIntegrationsByAccount({
              path: { accountId },
              urlParams: { limit: 100 },
            })
            .pipe(Effect.orDie),
        );
        return result.items;
      }

      const result = await effectApi.runPromise(
        client.integration
          .getTeamIntegrations({
            urlParams: {
              limit: 100,
              ...(includeDeleted ? { includeDeleted: true } : {}),
            },
          })
          .pipe(Effect.orDie),
      );

      return result.items;
    },
  });
}

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { type IntegrationWithChannelDataResponse } from "@redotech/effect-api-shared/models/shopper-ai/integration/integration";
